import React from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import {
  Container,
  Image,
  WysiwygField,
} from '@uncinc/uncinc-react-kitchen-sink';
import { CoreLink } from '@uncinc/react-drupal-core';
import { ImmutableEntity } from '@uncinc/react-drupal-core/types';
import { SearchMoreLikeThis, SearchResults } from '@uncinc/react-searchkit';
import { Hit, SearchMoreLikeThisQuery, SearchMoreLikeThisQueryFilter } from '@uncinc/react-searchkit/types';

import { NodeArticle } from '../../../../types/uncinc_profile/nodes';
import { ArticleMetadata, ParagraphsRenderer } from '../../../../components';

import './style.scss';
import { APP_OPTIONS, SEARCHKIT_HOST_URL } from '../../../../config/constants';

interface NodeArticlePageProps extends React.ComponentPropsWithoutRef<'div'> {
  entity: ImmutableEntity<NodeArticle>;
}

type ItemProps = {
  hit: Hit & {
    page_title: [string],
    url: [string],
  },
};

const Item: React.FC<ItemProps> = ({ hit }) => (
  <article className="Search__item">
    <h3 className="asH4"><CoreLink to={hit.url[0]}>{hit.page_title[0]}</CoreLink></h3>
    <strong>
      {hit.type}
    </strong>
    <div>
      {hit.created}
    </div>
  </article>
);

const NodeArticlePage: React.FC<NodeArticlePageProps> = ({ entity }) => {
  const { t } = useTranslation();
  const classes = classNames('NodeArticlePage');
  const article = entity.toJS();
  const title = article.title[0].value;
  const image = article.field_media_item[0]?.entity.field_media_image[0]?.source;
  const paragraphs = article.field_paragraphs;
  const body = article.body[0]?.processed;
  const publicationDate = article.field_publish_date?.[0]?.value || article.created[0]?.value;

  const query: SearchMoreLikeThisQuery = {
    fields: [
      'page_title',
      'body_summarized',
      'type',
      'body',
    ],
    like: [
      {
        _id: `entity:node/${article.nid[0].value}:${article.langcode[0].value}`,
      },
    ],
    min_term_freq: 1,
    max_query_terms: 12,
  };

  const filter: SearchMoreLikeThisQueryFilter = {
    filter: [
      {
        term: {
          type: {
            value: 'article',
          },
        },
      },
      {
        term: {
          type: {
            value: 'vacancy',
          },
        },
      },
    ],
  };

  return (
    <article itemType="http://schema.org/WebPage" className={classes}>
      <Container>
        <div className="NodeArticlePage__hero">
          {image && (
            <div className="NodeArticlePage__hero__image">
              <Image
                src={image}
                alt={title}
                lazyload={false}
              />
            </div>
          )}
          <div className="NodeArticlePage__hero__text">

            <ArticleMetadata>
              <span>
                {t('content_types.article.type')}
              </span>
              <span className="dot">&bull;</span>
              <ArticleMetadata.PublicationDate publicationDate={new Date(publicationDate)} />
              <span className="dot">&bull;</span>
              <ArticleMetadata.ReadTime readTime={4} />
            </ArticleMetadata>

            <h1 itemProp="name">{title}</h1>
            {body ? (
              <WysiwygField
                property="schema:text"
                html={body}
              />
            ) : null}
          </div>
        </div>
      </Container>

      <ParagraphsRenderer className="NodeArticlePage__paragraphs" paragraphs={paragraphs} />

      <Container>
        <SearchMoreLikeThis
          query={query}
          filter={filter}
          connection={{
            host: SEARCHKIT_HOST_URL,
          }}
          hitsPerPage={3}
          indexName={APP_OPTIONS?.parameters.searchkitIndexAllContent}
          searchSettings={{
            search_attributes: ['page_title', 'body_summarized'],
            result_attributes: ['type', 'created', 'page_title', 'body_summarized', 'url'],
          }}
        >
          <SearchResults
            renderItem={(hit) => <Item hit={hit} />}
            headerElement={<h2>More like this article</h2>}
            emptyState={<div />}
          />
        </SearchMoreLikeThis>
      </Container>
    </article>
  );
};

export default NodeArticlePage;
